import { formatIsoDate, subtractDay } from '@src/shared/lib';
import { useEffect, useRef } from 'react';
import type { Absence, TimelineSlot, VisibleRange } from '../../types';
import type { CalendarController } from './timeline';

type UseTimelineSelectionSyncParams = {
  absences: Absence[];
  activeSelection: TimelineSlot | null;
  calendar: Pick<CalendarController, 'select' | 'unselect'>;
  isDayView: boolean;
  visibleRange: VisibleRange;
};

export default function useTimelineSelectionSync({
  absences,
  activeSelection,
  calendar,
  isDayView,
  visibleRange,
}: UseTimelineSelectionSyncParams) {
  const syncedSelectionRef = useRef<string | null>(null);
  const isSyncingSelectionRef = useRef(false);

  useEffect(() => {
    if (
      !activeSelection?.start ||
      !activeSelection.end ||
      !activeSelection.resourceId
    ) {
      if (syncedSelectionRef.current !== null) {
        calendar.unselect();
        syncedSelectionRef.current = null;
      }
      return;
    }

    const syncKey = JSON.stringify({
      start: activeSelection.start,
      end: activeSelection.end,
      startTime: activeSelection.startTime,
      endTime: activeSelection.endTime,
      resourceId: activeSelection.resourceId,
      selectedIds: [...activeSelection.selectedIds].sort((a, b) => a - b),
      isDayView,
      visibleRangeStart: visibleRange.start.getTime(),
      visibleRangeEnd: visibleRange.end.getTime(),
    });

    if (syncedSelectionRef.current === syncKey) {
      return;
    }

    if (syncedSelectionRef.current !== null) {
      calendar.unselect();
    }

    isSyncingSelectionRef.current = true;

    if (isDayView) {
      let { startTime, endTime } = activeSelection;

      if (startTime == null && endTime == null) {
        // Check edited absence first (excluded from selectedIds)
        const editedId =
          activeSelection.editedAbsenceId ??
          activeSelection.editedAbsenceIds?.[0];
        const editedAbsence = editedId
          ? absences.find((a) => a.id === editedId)
          : null;

        if (
          editedAbsence &&
          !editedAbsence.allDay &&
          editedAbsence.startTime != null
        ) {
          startTime = editedAbsence.startTime;
          endTime = editedAbsence.endTime;
        } else if (activeSelection.selectedIds.length > 0) {
          const firstHourly = activeSelection.selectedIds
            .map((id) => absences.find((a) => a.id === id))
            .filter((a): a is Absence => !!a && !a.allDay)
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            )[0];

          if (firstHourly) {
            startTime = firstHourly.startTime;
            endTime = firstHourly.endTime;
          }
        }
      }

      const start = formatIsoDate(activeSelection.start, startTime ?? '00:00');
      const end = formatIsoDate(activeSelection.end, endTime ?? '23:59');

      calendar.select({
        start,
        end,
        resourceId: activeSelection.resourceId,
        allDay: false,
      });
    } else {
      const exclusiveEnd = formatIsoDate(
        subtractDay(new Date(activeSelection.end), -1),
      );

      calendar.select({
        start: activeSelection.start,
        end: exclusiveEnd,
        resourceId: activeSelection.resourceId,
        allDay: true,
      });
    }

    window.requestAnimationFrame(() => {
      isSyncingSelectionRef.current = false;
    });
    syncedSelectionRef.current = syncKey;
  }, [absences, activeSelection, calendar, isDayView, visibleRange]);

  return {
    isSyncingSelectionRef,
  };
}
